import {
  ApolloLink,
  Operation,
  FetchResult,
  Observable,
  NextLink,
} from '@apollo/client';
import type { ApolloCache, NormalizedCacheObject } from '@apollo/client';
import type { FreshnessCache } from '../gql/freshnessCache';
import { getMainDefinition } from '@apollo/client/utilities';

import { isEffectivelyOnline } from './connectivity';
import { setBackendReachable } from './backendReachability';
import {
  isRefreshUnavailableError,
  isSessionChangedError,
  isSessionEndedError,
} from '../gql/refreshFailure';

/**
 * Apollo Link that catches network errors on queries and falls back to cached data.
 *
 * Strategy:
 * - Mutations: always pass through (handled by OfflineQueueLink)
 * - Queries: always forwarded to Apollo Client's normal resolution.
 *   When offline, `client.prioritizeCacheValues = true` converts network-only
 *   to cache-first, so Apollo resolves from its normalized cache (robust).
 *   This link only acts as a SAFETY NET: if the downstream chain throws a
 *   network error, it attempts cache.readQuery() as a last-resort fallback.
 *
 * ── Techo de espera para las LECTURAS (2026-09-11) ────────────────────────
 * El respaldo de abajo solo entraba cuando la petición FALLABA. Con cobertura
 * mala no falla: se queda colgada (Chromium espera 300 s), y `navigator.onLine`
 * dice `true`, así que `prioritizeCacheValues` tampoco entra. Resultado medido
 * en tablet: el guardado se encolaba bien y la pantalla siguiente se quedaba
 * girando para siempre, porque su loader esperaba a queries que no volvían.
 *
 * Ahora una query que no contesta en `QUERY_DEADLINE_MS` se corta y se sirve de
 * la caché; si no hay nada cacheado, se emite un error de red — que es lo que
 * los loaders ya saben tratar— en vez de dejar la ruedecita infinita.
 *
 * Why NOT short-circuit offline queries:
 *   Apollo's internal cache resolution (via fetchPolicy: cache-first) is more
 *   robust than cache.readQuery(). It handles partial matches, normalized
 *   references, and type policies. Short-circuiting with readQuery() is too
 *   strict and misses data that Apollo could resolve from the normalized cache.
 */
/**
 * Techo por lectura. Generoso a propósito: una query sana tarda menos de un
 * segundo, así que 12 s solo corta lo que ya no va a volver. El de las
 * escrituras es 25 s porque ahí manda la transacción del backend.
 */
export const QUERY_DEADLINE_MS = 12_000;

/**
 * Consultas que pueden tardar de verdad y a las que NO se les pone techo: el
 * usuario las lanza a mano y espera con su propio indicador, y cortarlas
 * convertiría un informe lento en un error.
 */
export const UNBOUNDED_QUERIES: ReadonlySet<string> = new Set([
  'FetchLaborHoursReport',
  'GetActionBudgetItemsHistorical',
  'GetSurveillanceRouteForReport',
  'RoadPkMesh',
]);

/** Devuelve el techo aplicable, o `null` si la operación no lo lleva. */
export function resolveQueryDeadline(
  operationName: string,
  context?: { queryTimeoutMs?: number | null },
): number | null {
  if (context && 'queryTimeoutMs' in context) {
    const valor = context.queryTimeoutMs;
    return typeof valor === 'number' && valor > 0 ? valor : null;
  }
  if (UNBOUNDED_QUERIES.has(operationName)) return null;
  return QUERY_DEADLINE_MS;
}

export class OfflineCacheFallbackLink extends ApolloLink {
  private cache: ApolloCache<NormalizedCacheObject>;

  constructor(cache: ApolloCache<NormalizedCacheObject>) {
    super();
    this.cache = cache;
  }

  request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    // `definitions[0]` puede ser un fragment (las queries del timer lo llevan
    // PRIMERO); getMainDefinition salta los fragments — mismo fix que
    // OfflineQueueLink el 2026-07-21.
    const definition = getMainDefinition(operation.query);
    const isQuery =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'query';

    // Mutations pass through — handled by OfflineQueueLink
    if (!isQuery) return forward(operation);

    // Always forward queries to Apollo Client's normal resolution.
    // prioritizeCacheValues handles offline by converting network-only → cache-first.
    // This link wraps the chain to catch network errors as a safety net.
    const deadlineMs = resolveQueryDeadline(
      operation.operationName,
      operation.getContext() as { queryTimeoutMs?: number | null },
    );

    return new Observable<FetchResult>((observer) => {
      let resuelto = false;
      let temporizador: ReturnType<typeof setTimeout> | null = null;

      /** Sirve lo cacheado y cierra; si no hay nada, `false`. */
      const servirDeCache = (): boolean => {
        const cachedData = this.readCache(operation);
        if (cachedData === null) return false;
        resuelto = true;
        observer.next({ data: cachedData });
        observer.complete();
        return true;
      };

      const subscription = forward(operation).subscribe({
        next: (result) => {
          resuelto = true;
          observer.next(result);
        },
        error: (error) => {
          if (temporizador) clearTimeout(temporizador);
          if (resuelto) return;
          if (this.isNetworkError(error) && servirDeCache()) return;
          resuelto = true;
          observer.error(error);
        },
        complete: () => {
          if (temporizador) clearTimeout(temporizador);
          observer.complete();
        },
      });

      if (deadlineMs !== null) {
        temporizador = setTimeout(() => {
          if (resuelto) return;
          // La petición no ha vuelto y no va a volver a tiempo: se corta y se
          // tira de caché. Sin caché, un error de red —lo que los loaders ya
          // saben tratar— en vez de dejar la pantalla girando para siempre.
          subscription.unsubscribe();
          // Misma evidencia que en las escrituras: si una lectura no vuelve a
          // tiempo, el backend no está. Así el resto de la app deja de
          // intentarlo hasta que algo responda.
          setBackendReachable(false);
          if (servirDeCache()) return;
          resuelto = true;
          observer.error(
            Object.assign(
              new Error(
                `La consulta ${operation.operationName} no respondió en ${deadlineMs} ms`,
              ),
              { networkError: { name: 'QueryDeadlineError' } },
            ),
          );
        }, deadlineMs);
      }

      return () => {
        if (temporizador) clearTimeout(temporizador);
        subscription.unsubscribe();
      };
    });
  }

  private readCache(operation: Operation): any | null {
    try {
      const read = () =>
        this.cache.readQuery({
          query: operation.query,
          variables: operation.variables,
        });
      // Stale-if-error: aquí SÍ se quiere lo caducado, que `FreshnessCache` lee
      // como ausente en cualquier otro sitio (spec 2026-10-01 § 7).
      const cache = this.cache as Partial<Pick<FreshnessCache, 'readStale'>>;
      return typeof cache.readStale === 'function' ? cache.readStale(read) : read();
    } catch {
      return null;
    }
  }

  private isNetworkError(error: any): boolean {
    // Una consulta de una sesión muerta o cambiada no se contesta desde la caché,
    // ni siquiera sin red (spec § 12.1), y antes de
    // mirar `networkError` (Apollo envuelve ahí el error del link): la caché es de otra sesión.
    if (isSessionEndedError(error) || isSessionChangedError(error)) return false;
    // No se pudo refrescar el token (red, plazo, 5xx): la sesión sigue viva y
    // lo cacheado vale más que una pantalla vacía (spec 2026-10-01 § 7.2).
    if (isRefreshUnavailableError(error)) return true;
    if (error?.networkError && !error.networkError.statusCode) return true;
    if (error?.message?.includes('Failed to fetch')) return true;
    if (error?.message?.includes('NetworkError')) return true;
    // Sin red o con el servidor comprobado caído (cortacircuitos, spec 2026-10-09
    // D6): lo que sube es el último intento —a menudo un 503 del proxy— y lo
    // guardado vale más que una pantalla de error.
    if (!isEffectivelyOnline()) return true;
    return false;
  }
}
